import {
  ArrowPathIcon,
  BackwardIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ForwardIcon,
  PauseIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import type { Finding, Interaction } from "@/types";
import { formatDuration } from "@/utils/formatters";
import { frameDurationSeconds, terminalStatuses, viewportDimensions } from "../constants";
import { stepForFrame, stickyCursorCenter, timelinePercentage } from "../helpers";
import ControlButton from "./ControlButton";
import CursorOverlay from "./CursorOverlay";
import EmptyPlayerState from "./EmptyPlayerState";
import PlayerFindingCard from "./PlayerFindingCard";

type InteractionPlayerProps = {
  currentFrame: number;
  interaction: Interaction;
  onCurrentFrameChange: (frame: number) => void;
  onPlayingChange: (playing: boolean) => void;
  playing: boolean;
};

export default function InteractionPlayer({
  currentFrame,
  interaction,
  onCurrentFrameChange,
  onPlayingChange,
  playing,
}: InteractionPlayerProps) {
  const [showHeuristicViolations, setShowHeuristicViolations] = useState(true);
  const screenshots = interaction.screenshots;
  const currentScreenshot = screenshots[currentFrame];
  const currentStep = currentScreenshot ? stepForFrame(interaction.steps, screenshots, currentFrame) : undefined;
  const cursorPosition = stickyCursorCenter(interaction.steps, screenshots, currentFrame);
  const showCursorPing = currentStep?.action_type === "click" || currentStep?.action_type === "fill";
  const findingsByScreenshot = (interaction.usability_evaluation?.findings || []).reduce<Record<string, Finding[]>>(
    (grouped, finding) => {
      if (!finding.screenshot_id) return grouped;

      grouped[finding.screenshot_id] = [...(grouped[finding.screenshot_id] || []), finding];
      return grouped;
    },
    {},
  );
  const currentFrameFindings = currentScreenshot ? findingsByScreenshot[currentScreenshot.id] || [] : [];
  const terminalInteraction = terminalStatuses.includes(interaction.status);
  const totalDuration = Math.max((screenshots.length - 1) * frameDurationSeconds, 0);
  const currentTime = Math.min(currentFrame * frameDurationSeconds, totalDuration);
  const viewportStyle = viewportDisplayStyle(interaction.viewport);

  return (
    <div className="interaction-player card card-body min-h-[672px] flex-grow scroll-m-3">
      <div className="relative mb-4 flex items-center justify-between">
        <h2 className="mb-0 text-xl font-semibold text-gray-800">Interaction Replay</h2>
        {interaction.status === "running" ? (
          <div className="flex items-center rounded-md bg-primary px-3 py-1 text-sm text-white">
            <span className="mr-2 h-2 w-2 animate-pulse rounded-full bg-white" />
            Recording
          </div>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-lg">
        <div className={viewportShellClass(interaction.viewport)} style={viewportStyle}>
          <div className="flex h-full items-center justify-center bg-gray-100">
            {currentScreenshot?.image_url ? (
              <div className={frameContainerClass(interaction.viewport)} style={viewportStyle}>
                {screenshots.map((screenshot, index) =>
                  screenshot.image_url ? (
                    <img
                      key={screenshot.id}
                      src={screenshot.image_url}
                      alt={`Interaction frame ${index + 1}`}
                      className={`screenshot-frame object-contain ${index === currentFrame ? "active" : ""}`}
                      loading="lazy"
                    />
                  ) : null,
                )}
                <CursorOverlay position={cursorPosition} showPing={showCursorPing} viewport={interaction.viewport} />
              </div>
            ) : (
              <EmptyPlayerState interaction={interaction} />
            )}
          </div>
        </div>

        {terminalInteraction ? (
          <div className="bg-gray-900 px-6 py-4">
            <button
              type="button"
              className="relative mb-4 h-2 w-full cursor-pointer rounded-full bg-gray-700"
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                const percentage = (event.clientX - rect.left) / rect.width;
                onCurrentFrameChange(Math.floor((screenshots.length - 1) * percentage));
              }}
              aria-label="Replay timeline"
            >
              <span
                className="absolute left-0 top-0 h-full rounded-full bg-primary"
                style={{
                  width: `${timelinePercentage(currentFrame, screenshots.length)}%`,
                }}
              />
              {screenshots.map((screenshot, index) => {
                const findings = findingsByScreenshot[screenshot.id] || [];
                if (findings.length === 0) return null;

                return (
                  <span key={screenshot.id} className="group">
                    <span
                      className="absolute top-0 z-20 mt-1 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-warning opacity-0 group-hover:opacity-20"
                      style={{
                        left: `${timelinePercentage(index, screenshots.length)}%`,
                      }}
                    />
                    <span
                      className="absolute top-0 mt-1 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-warning opacity-0 group-hover:opacity-100"
                      style={{
                        left: `${timelinePercentage(index, screenshots.length)}%`,
                      }}
                    />
                    <span
                      className="absolute top-0 z-10 mt-1 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-warning"
                      style={{
                        left: `${timelinePercentage(index, screenshots.length)}%`,
                      }}
                    >
                      <span className="absolute bottom-4 left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-3 py-2 text-xs text-white group-hover:block">
                        {findings.length} {findings.length === 1 ? "issue" : "issues"}
                      </span>
                    </span>
                  </span>
                );
              })}
            </button>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">{formatDuration(currentTime)}</div>
              <div className="flex items-center space-x-4 lg:-ml-9">
                <ControlButton
                  label="Restart"
                  hiddenOnSmallScreen
                  onClick={() => {
                    onCurrentFrameChange(0);
                    onPlayingChange(screenshots.length > 1);
                  }}
                >
                  <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
                </ControlButton>
                <ControlButton
                  label="Previous frame"
                  onClick={() => onCurrentFrameChange(Math.max(currentFrame - 1, 0))}
                >
                  <BackwardIcon className="h-5 w-5" aria-hidden="true" />
                </ControlButton>
                <button
                  type="button"
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-sm transition-colors hover:bg-primary/90"
                  onClick={() => onPlayingChange(!playing && currentFrame < screenshots.length - 1)}
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? (
                    <PauseIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" />
                  )}
                </button>
                <ControlButton
                  label="Next frame"
                  onClick={() => onCurrentFrameChange(Math.min(currentFrame + 1, screenshots.length - 1))}
                >
                  <ForwardIcon className="h-5 w-5" aria-hidden="true" />
                </ControlButton>
              </div>
              <div className="text-sm text-gray-400">{formatDuration(totalDuration)}</div>
            </div>
          </div>
        ) : null}
      </div>

      {terminalInteraction ? (
        <div className="mt-6">
          {currentFrameFindings.length > 0 ? (
            <div>
              <button
                type="button"
                className="flex w-full cursor-pointer flex-col items-start justify-between gap-y-2 lg:flex-row lg:items-center lg:gap-y-0"
                onClick={() => setShowHeuristicViolations((show) => !show)}
              >
                <h3 className="mb-0 flex items-center text-lg font-semibold text-warning-dark">
                  <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-warning" aria-hidden="true" />
                  {currentFrameFindings.length}{" "}
                  {currentFrameFindings.length === 1 ? "usability issue" : "usability issues"}
                </h3>
                <span className="flex items-center gap-x-2 text-gray-500 transition-colors hover:text-gray-700">
                  <span className="border-b border-gray-300">
                    {showHeuristicViolations ? "Hide issues" : "Show issues"}
                  </span>
                  {showHeuristicViolations ? (
                    <ChevronUpIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </span>
              </button>
              {showHeuristicViolations ? (
                <div className="mt-6 space-y-4">
                  {currentFrameFindings
                    .sort((first, second) => (second.severity || 0) - (first.severity || 0))
                    .map((finding) => (
                      <PlayerFindingCard key={finding.id} finding={finding} />
                    ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex cursor-pointer items-center justify-between">
              <h3 className="mb-0 flex items-center text-lg font-semibold text-green-700">
                <CheckIcon className="mr-2 h-5 w-5 text-green-600" aria-hidden="true" />
                All clear, no usability issues identified on this frame
              </h3>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function viewportDisplayStyle(viewport: string | null) {
  const size = viewport === "mobile" ? viewportDimensions.mobile : viewportDimensions.desktop;

  return {
    aspectRatio: `${size.width} / ${size.height}`,
    maxWidth: viewport === "mobile" ? `${size.width}px` : undefined,
  };
}

function viewportShellClass(viewport: string | null) {
  if (viewport === "mobile") {
    return "relative mx-auto";
  }

  return "relative";
}

function frameContainerClass(viewport: string | null) {
  const base = "relative h-full max-h-full w-full max-w-full";

  if (viewport === "mobile") {
    return `${base} overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm`;
  }

  return base;
}
